import { useState, useEffect } from "react";
import { Bell, BellDot } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

interface Notification {
  id: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

const NotificationBell = () => {
  const [hasUnread, setHasUnread] = useState(false);

  const { data: notifications = [], refetch } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Notification[];
    },
  });

  useEffect(() => {
    setHasUnread(notifications.some((n) => !n.is_read));
  }, [notifications]);

  useEffect(() => {
    const channel = supabase
      .channel("schema-db-changes")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  const markAsRead = async (id: string) => {
    const { error } = await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("id", id);

    if (!error) {
      refetch();
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={`relative h-10 w-10 rounded-full transition-all duration-200 ${
            hasUnread
              ? "bg-primary/10 hover:bg-primary/20 shadow-lg ring-2 ring-primary"
              : "hover:bg-muted"
          }`}
          size="icon"
        >
          {hasUnread ? (
            <BellDot className="h-5 w-5 text-primary animate-pulse" />
          ) : (
            <Bell className="h-5 w-5" />
          )}
          {hasUnread && (
            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-primary ring-2 ring-background" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 p-2" align="end">
        <div className="flex items-center justify-between px-2 py-2 mb-2 border-b">
          <h4 className="font-semibold text-sm">Notifications</h4>
          {hasUnread && (
            <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
              New
            </span>
          )}
        </div>
        <DropdownMenuGroup>
          <ScrollArea className="h-[300px] px-2">
            {notifications.length === 0 ? (
              <div className="text-center py-4 text-sm text-muted-foreground">
                No notifications
              </div>
            ) : (
              notifications.map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  className={`mb-2 cursor-pointer rounded-lg p-3 transition-all duration-200 ${
                    !notification.is_read
                      ? "bg-gradient-to-r from-primary/5 to-primary/10 hover:from-primary/10 hover:to-primary/20 shadow-sm"
                      : "hover:bg-muted"
                  }`}
                  onClick={() => markAsRead(notification.id)}
                >
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium leading-none">
                        {notification.title}
                        {!notification.is_read && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-primary px-1.5 py-0.5 text-xs font-medium text-primary-foreground">
                            New
                          </span>
                        )}
                      </p>
                      <span className="text-xs text-gray-500">
                        {format(new Date(notification.created_at), "MMM d, h:mm a")}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">{notification.message}</p>
                  </div>
                </DropdownMenuItem>
              ))
            )}
          </ScrollArea>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;